import { createSlice } from '@reduxjs/toolkit';

const MAX_UNDO = 50;

// Temporary ID counter for new nodes/edges (stays within Java Integer range)
let tempIdCounter = -1;

/** Returns a negative temporary ID that won't collide with DB IDs (which are positive). */
export function nextTempId() {
  return tempIdCounter--;
}

const initialState = {
  currentFlow: null, // { id, name, nodes: [], edges: [], metadata: {} }
  selectedNodeId: null,
  executionState: null,
  executionHistory: [],
  activeExecutionResult: null, // Full execution result with nodeResults for on-canvas display
  selectedIterationIndex: null, // Which iteration is selected in Mode B
  iterationResults: null, // Per-node results for the selected iteration
  unsavedChanges: false,
  undoStack: [],
  redoStack: [],
};

const advancedOrchestrationSlice = createSlice({
  name: 'advancedOrchestration',
  initialState,
  reducers: {
    setFlow(state, action) {
      state.currentFlow = action.payload;
      state.unsavedChanges = false;
      state.undoStack = [];
      state.redoStack = [];
      state.activeExecutionResult = null;
      state.selectedIterationIndex = null;
      state.iterationResults = null;
      state.executionHistory = [];
      state.selectedNodeId = null;
    },
    addNode(state, action) {
      if (!state.currentFlow) return;
      pushUndo(state);
      state.currentFlow.nodes.push(action.payload);
      state.unsavedChanges = true;
      state.activeExecutionResult = null;
    },
    removeNode(state, action) {
      if (!state.currentFlow) return;
      pushUndo(state);
      const nodeId = action.payload;
      state.currentFlow.nodes = state.currentFlow.nodes.filter(n => n.id !== nodeId);
      state.currentFlow.edges = state.currentFlow.edges.filter(
        e => e.sourceNodeId !== nodeId && e.targetNodeId !== nodeId
      );
      if (state.selectedNodeId === nodeId) state.selectedNodeId = null;
      state.unsavedChanges = true;
      state.activeExecutionResult = null;
    },
    updateNode(state, action) {
      if (!state.currentFlow) return;
      pushUndo(state);
      const idx = state.currentFlow.nodes.findIndex(n => n.id === action.payload.id);
      if (idx !== -1) state.currentFlow.nodes[idx] = { ...state.currentFlow.nodes[idx], ...action.payload };
      state.unsavedChanges = true;
    },
    addEdge(state, action) {
      if (!state.currentFlow) return;
      pushUndo(state);
      state.currentFlow.edges.push(action.payload);
      state.unsavedChanges = true;
      state.activeExecutionResult = null;
    },
    removeEdge(state, action) {
      if (!state.currentFlow) return;
      pushUndo(state);
      state.currentFlow.edges = state.currentFlow.edges.filter(e => e.id !== action.payload);
      state.unsavedChanges = true;
      state.activeExecutionResult = null;
    },
    setSelectedNode(state, action) {
      state.selectedNodeId = action.payload;
    },
    setExecutionState(state, action) {
      state.executionState = action.payload;
    },
    updateNodeStatus(state, action) {
      // action.payload: { nodeId, status, ... }
      if (!state.executionState) state.executionState = { nodeStatuses: {} };
      state.executionState.nodeStatuses = {
        ...state.executionState.nodeStatuses,
        [action.payload.nodeId]: action.payload,
      };
    },
    setExecutionHistory(state, action) {
      state.executionHistory = action.payload;
    },
    setActiveExecutionResult(state, action) {
      state.activeExecutionResult = action.payload;
    },
    clearExecutionResult(state) {
      state.activeExecutionResult = null;
      state.selectedIterationIndex = null;
      state.iterationResults = null;
    },
    setSelectedIteration(state, action) {
      state.selectedIterationIndex = action.payload;
    },
    setIterationResults(state, action) {
      state.iterationResults = action.payload;
    },
    undo(state) {
      if (state.undoStack.length === 0 || !state.currentFlow) return;
      state.redoStack.push({ nodes: [...state.currentFlow.nodes], edges: [...state.currentFlow.edges] });
      const prev = state.undoStack.pop();
      state.currentFlow.nodes = prev.nodes;
      state.currentFlow.edges = prev.edges;
      state.unsavedChanges = true;
    },
    redo(state) {
      if (state.redoStack.length === 0 || !state.currentFlow) return;
      state.undoStack.push({ nodes: [...state.currentFlow.nodes], edges: [...state.currentFlow.edges] });
      const next = state.redoStack.pop();
      state.currentFlow.nodes = next.nodes;
      state.currentFlow.edges = next.edges;
      state.unsavedChanges = true;
    },
    /**
     * Merge into the flow's metadata (identities, aiPrompt, …) without the
     * reset that setFlow performs. Persisted as flowMetadata on save.
     */
    updateFlowMetadata(state, action) {
      if (!state.currentFlow) return;
      state.currentFlow.metadata = { ...(state.currentFlow.metadata || {}), ...action.payload };
      state.unsavedChanges = true;
    },
    markSaved(state) {
      state.unsavedChanges = false;
    },
    markUnsaved(state) {
      state.unsavedChanges = true;
    },
  },
});

function pushUndo(state) {
  if (!state.currentFlow) return;
  state.undoStack.push({ nodes: [...state.currentFlow.nodes], edges: [...state.currentFlow.edges] });
  if (state.undoStack.length > MAX_UNDO) state.undoStack.shift();
  state.redoStack = [];
}

export const {
  setFlow, addNode, removeNode, updateNode, addEdge, removeEdge,
  setSelectedNode, setExecutionState, updateNodeStatus, setExecutionHistory,
  setActiveExecutionResult, clearExecutionResult,
  setSelectedIteration, setIterationResults,
  undo, redo, markSaved, markUnsaved, updateFlowMetadata,
} = advancedOrchestrationSlice.actions;

export default advancedOrchestrationSlice.reducer;
